import React, { useState } from 'react';
import { useCookieConsent } from '../lib/cookie-consent-context';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { X, Settings, Cookie, Shield, BarChart3, Target } from 'lucide-react';

const CookieBanner = () => {
  const {
    showBanner,
    preferences,
    acceptAll,
    rejectOptional,
    savePreferences,
    setShowBanner
  } = useCookieConsent();

  const [showDetails, setShowDetails] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(preferences);

  // Si le banner n'est pas affiché, ne rien rendre
  if (!showBanner) return null;

  const handleTogglePreference = (type) => {
    if (type === 'necessary') return; // Les cookies nécessaires ne peuvent pas être désactivés
    
    setTempPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSaveCustom = () => {
    savePreferences(tempPreferences);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Cookies nécessaires',
      description: 'Ces cookies sont indispensables au fonctionnement du site. Ils permettent de naviguer sur le site, d\'utiliser ses fonctionnalités de base et de traiter les paiements en toute sécurité.',
      icon: Shield,
      required: true,
      examples: ['Session utilisateur', 'Paiements Stripe', 'Sécurité', 'Prévention fraude']
    },
    {
      id: 'analytics',
      name: 'Cookies analytiques',
      description: 'Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site en collectant des informations anonymes.',
      icon: BarChart3,
      required: false,
      examples: ['Google Analytics', 'Statistiques de visite', 'Amélioration du site']
    },
    {
      id: 'marketing',
      name: 'Cookies marketing',
      description: 'Ces cookies sont utilisés pour personnaliser les publicités et analyser l\'efficacité de nos campagnes.',
      icon: Target,
      required: false,
      examples: ['Publicités ciblées', 'Réseaux sociaux', 'Remarketing']
    }
  ];

  if (showDetails) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cookie className="h-6 w-6" />
                <CardTitle>Paramètres des cookies</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CardDescription>
              Gérez vos préférences de cookies. Vous pouvez modifier ces paramètres à tout moment.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {cookieTypes.map((type) => {
              const Icon = type.icon;
              const isEnabled = tempPreferences[type.id];
              
              return (
                <div key={type.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="h-5 w-5" />
                        <h3 className="font-semibold">{type.name}</h3>
                        {type.required && (
                          <Badge variant="secondary" className="text-xs">
                            Requis
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {type.description}
                      </p>
                      <div className="text-xs text-muted-foreground">
                        <strong>Exemples :</strong> {type.examples.join(', ')}
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleTogglePreference(type.id)}
                      disabled={type.required}
                    />
                  </div>
                </div>
              );
            })}
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveCustom} variant="outline" className="flex-1">
                Sauvegarder mes préférences
              </Button>
              <Button onClick={acceptAll}>
                Tout accepter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

      return (
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t shadow-lg z-40 py-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="h-6 w-6 mt-1 text-primary flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Ce site utilise des cookies pour améliorer votre expérience et analyser le trafic.
                </p>
              </div>
            </div>
            
            <div className="flex flex-row gap-3 w-full lg:w-auto lg:flex-shrink-0">
              <Button
                onClick={() => setShowDetails(true)}
                variant="outline"
                size="default"
                className="flex-1 lg:flex-none text-sm h-10 px-4"
              >
                <Settings className="h-4 w-4 mr-2" />
                Options
              </Button>
              <Button
                onClick={acceptAll}
                size="default"
                className="flex-1 lg:flex-none text-sm h-10 px-4"
              >
                Accepter
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
};

export default CookieBanner;
